import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Clock, RefreshCw, ExternalLink, Coins, Wallet } from "lucide-react";

const STATUS_FILTERS = [
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Rejected", value: "rejected" },
  { label: "All", value: "" },
];

const PendingPurchasesTab = () => {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/purchases", { params: { status: status || undefined } });
      setRows(data);
    } catch { toast.error("Failed to load purchases"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/purchases/${row.id}/approve`, { reason: "" });
      toast.success(`Approved · ${row.product_name}`);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const reject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor.id);
    try {
      await api.post(`/admin/purchases/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Purchase rejected");
      setRejectFor(null); setRejectReason("");
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const totals = rows.reduce(
    (acc, r) => {
      acc.coins += r.greencoins || r.green_points || 0;
      acc.cash += r.cashback || 0;
      return acc;
    },
    { coins: 0, cash: 0 }
  );

  return (
    <div className="space-y-5" data-testid="pending-purchases-tab">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value)}
            className={`text-xs px-3 py-1.5 rounded-full ${status === s.value ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            data-testid={`pending-filter-${s.value || "all"}`}
          >
            {s.label}
          </button>
        ))}
        <button onClick={fetchRows} className="ml-auto text-xs px-3 py-1.5 rounded-full bg-muted flex items-center gap-1" data-testid="pending-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="card-premium p-5" data-testid="pending-stat-count">
          <div className="inline-grid place-items-center h-9 w-9 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <Clock className="h-4 w-4" />
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Rows</div>
          <div className="mono text-2xl font-bold mt-1">{rows.length}</div>
        </div>
        <div className="card-premium p-5" data-testid="pending-stat-cash">
          <div className="inline-grid place-items-center h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Wallet className="h-4 w-4" />
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Cashback</div>
          <div className="mono text-2xl font-bold mt-1">₹{totals.cash}</div>
        </div>
        <div className="card-premium p-5" data-testid="pending-stat-coins">
          <div className="inline-grid place-items-center h-9 w-9 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <Coins className="h-4 w-4" />
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">GreenCoins</div>
          <div className="mono text-2xl font-bold mt-1">{totals.coins}</div>
        </div>
      </div>

      <div className="card-premium overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
          <div className="col-span-4">Product & buyer</div>
          <div className="col-span-2">Clicked</div>
          <div className="col-span-2 text-right">Cashback</div>
          <div className="col-span-1 text-right">Coins</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {rows.length === 0 && (
          <div className="py-14 text-center text-sm text-muted-foreground">No {status || "purchase"} records.</div>
        )}
        {rows.map((r) => {
          const isBusy = busyId === r.id;
          const isPending = (r.status || "pending") === "pending";
          return (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-border/40 last:border-b-0 hover:bg-muted/30 text-sm" data-testid={`pending-row-${r.id}`}>
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <img src={r.product_image} alt="" className="h-10 w-10 rounded-xl object-cover shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{r.product_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.user_name || r.user_email || r.user_id?.slice(0, 8)}
                    {r.click_count > 1 && <span className="ml-1 text-amber-600">· {r.click_count} clicks</span>}
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">
                {new Date(r.affiliate_click_at || r.created_at).toLocaleString()}
              </div>
              <div className="col-span-2 text-right mono font-semibold">₹{r.cashback}</div>
              <div className="col-span-1 text-right mono font-semibold text-emerald-600">+{r.greencoins || r.green_points}</div>
              <div className="col-span-3 flex justify-end items-center gap-1">
                {isPending ? (
                  <>
                    <button
                      onClick={() => approve(r)}
                      disabled={isBusy}
                      className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`approve-${r.id}`}
                    ><Check className="h-3 w-3" /> Approve</button>
                    <button
                      onClick={() => { setRejectFor(r); setRejectReason(""); }}
                      disabled={isBusy}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted text-foreground flex items-center gap-1 hover:bg-muted/70 disabled:opacity-50"
                      data-testid={`reject-${r.id}`}
                    ><X className="h-3 w-3" /> Reject</button>
                  </>
                ) : (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.status === "confirmed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
                    {r.status}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setRejectFor(null)} data-testid="reject-modal">
          <div className="card-premium max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold">Reject purchase</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Rewards for <span className="font-semibold">{rejectFor.product_name}</span> ({rejectFor.user_email || rejectFor.user_name}) will be removed.
            </p>
            <label className="block mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Reason (optional)</label>
            <textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Not confirmed by merchant / returned / duplicate…"
              className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setRejectFor(null)} className="text-xs px-4 py-2 rounded-full bg-muted">Cancel</button>
              <button onClick={reject} className="text-xs px-4 py-2 rounded-full bg-red-600 text-white" data-testid="confirm-reject">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingPurchasesTab;
